import { useVirtualizer } from "@tanstack/react-virtual";
import classNames from "classnames";
import isFunction from "lodash-es/isFunction";
import {
  CSSProperties,
  useCallback,
  useRef,
  useState,
  useTransition,
} from "react";

import useActiveEditorView from "pages/DevelopWorkspace/contexts/ActiveEditorViewContext/hooks/useActiveEditorView";

import { TreeNode } from "./TreeNode";
import { ItemDataType, TreeNodeType } from "./types";
import { useFlattenTreeData } from "./useFlattenTreeData";
import { useGetTreeNodeChildren } from "./useGetTreeNodeChildren";
import {
  EMPTY_TYPE,
  getActiveItem,
  onTreeKeyDown,
  toggleExpand,
} from "./utils";

import styles from "./styles.module.scss";

type Props = {
  data: ItemDataType[];
  searchKeyword?: string;
  nodeClassName?: string;
  treeClassName?: string;
  getChildren?: (
    node: ItemDataType
  ) => ItemDataType[] | Promise<ItemDataType[] | null> | null;
  renderLabel: (node: TreeNodeType, loading?: boolean) => React.ReactNode;
  emptyState: React.ReactElement;
  emptySearchState: React.ReactElement;
  controlledExpandedItems?: string[];
  onExpand?: (expandedItems: string[]) => void;
};

const itemSize = () => 30;

export const SHIFT_CLICK_NODE_TYPES = [
  "database",
  "table",
  "column",
  "information_schema_table",
  "information_schema_column",
  "external_table",
  "external_table_column",
  "view",
];

const useTreeHandlers = ({
  setFocusItem,
  focusNextItem,
  focusPreviousItem,
  handleRightArrow,
  handleLeftArrow,
  handleEnter,
}: {
  setFocusItem: React.Dispatch<React.SetStateAction<string | null>>;
  focusNextItem: () => void;
  focusPreviousItem: () => void;
  handleRightArrow: () => void;
  handleLeftArrow: () => void;
  handleEnter: () => void;
}) => {
  const treeViewRef = useRef<HTMLDivElement>(null);

  const handleTreeBlur: React.FocusEventHandler = event => {
    if (!treeViewRef.current) {
      return;
    }
    const node: any = event.relatedTarget?.parentNode;

    // check if any parent of the parent is menu, go up to the root
    let parentNode = node;
    while (parentNode) {
      if (parentNode.role === "menu") {
        return;
      }
      parentNode = parentNode.parentNode;
    }

    setFocusItem(null);
  };

  const handleTreeKeyDown: React.KeyboardEventHandler<
    HTMLDivElement
  > = event => {
    if (!treeViewRef.current) {
      return;
    }

    if (!treeViewRef.current.contains(event.target as Node)) {
      return;
    }

    onTreeKeyDown(event, {
      down: () => focusNextItem(),
      up: () => focusPreviousItem(),
      left: handleLeftArrow,
      right: handleRightArrow,
      enter: handleEnter,
    });
  };

  return {
    treeViewRef,
    handleTreeKeyDown,
    handleTreeBlur,
  };
};

const TreeInner = (props: {
  loadingNodeValues: string[];
  formattedNodes: TreeNodeType[];
  focusNextItem: () => void;
  focusPreviousItem: () => void;
  handleRightArrow: () => void;
  handleLeftArrow: () => void;
  handleEnter: () => void;
  handleExpand: (node: TreeNodeType) => void;
  setFocusItem: React.Dispatch<React.SetStateAction<string | null>>;
  focusItem: string | null;
  treeData: ItemDataType[];
  searchKeyword?: string;
  renderLabel: (node: TreeNodeType, loading?: boolean) => React.ReactNode;
  nodeClassName?: string;
  treeClassName?: string;
  emptySearchState: React.ReactElement;
  emptyState: React.ReactElement;
  expandedItems: string[];
}) => {
  const {
    treeData,
    searchKeyword,
    renderLabel,
    nodeClassName,
    treeClassName,
    emptySearchState,
    emptyState,
    formattedNodes,
    focusItem,
    focusNextItem,
    focusPreviousItem,
    handleRightArrow,
    handleLeftArrow,
    handleEnter,
    loadingNodeValues,
    handleExpand,
    setFocusItem,
    expandedItems,
  } = props;

  const { insertTextAtCursorPosition } = useActiveEditorView();
  const scrollRef = useRef<HTMLDivElement>(null);

  const { handleTreeBlur, handleTreeKeyDown, treeViewRef } = useTreeHandlers({
    focusNextItem,
    setFocusItem,
    focusPreviousItem,
    handleEnter,
    handleLeftArrow,
    handleRightArrow,
  });

  const handleShiftClick = useCallback(
    (node: TreeNodeType) => {
      if (
        node.label &&
        node.type &&
        SHIFT_CLICK_NODE_TYPES.includes(node.type)
      ) {
        insertTextAtCursorPosition(node.label);
      }
    },
    [insertTextAtCursorPosition]
  );

  const getTreeNodeProps = useCallback(
    (node: any, layer: number, index?: number) => {
      return {
        value: node.value,
        label: node.label,
        index,
        layer,
        loading: loadingNodeValues.some(item => item === node.value),
        expand: node.expand,
        focus: node.value === focusItem,
        visible: node.visible,
        children: node.children,
        nodeData: node,
        onExpand: handleExpand,
        onShiftClick: handleShiftClick,
        renderLabel,
      };
    },
    [renderLabel, handleShiftClick, handleExpand, focusItem, loadingNodeValues]
  );

  const renderNode = useCallback(
    ({
      index,
      style,
      data,
    }: {
      index: number;
      style: CSSProperties;
      data: TreeNodeType[];
    }) => {
      const node = data[index];
      const { value, layer, visible } = node as TreeNodeType;

      const expand = expandedItems.includes(value as string);

      if (!visible) {
        return null;
      }

      const nodeProps = {
        ...getTreeNodeProps({ ...node, expand }, layer),
        style,
        hasChildren: node.hasChildren,
      };

      return (
        <TreeNode
          key={node.value}
          className={nodeClassName}
          {...nodeProps}
        />
      );
    },
    [expandedItems, getTreeNodeProps, nodeClassName]
  );

  const rowVirtualizer = useVirtualizer({
    count: formattedNodes.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: itemSize,
    overscan: 5,
  });

  if (searchKeyword && !formattedNodes.length) {
    return emptySearchState;
  }

  if (!searchKeyword && !formattedNodes.length && !treeData.length) {
    return emptyState;
  }

  return (
    <div
      ref={treeViewRef}
      className={classNames(styles.tree, treeClassName)}
      onKeyDown={handleTreeKeyDown}
      onBlur={handleTreeBlur}
      role="tree"
      tabIndex={0}
    >
      <div className={styles.nodes}>
        <div
          style={{
            height: "100%",
            width: "100%",
            overflow: "auto",
          }}
          ref={scrollRef}
        >
          <div
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              width: "100%",
              position: "relative",
            }}
          >
            {rowVirtualizer.getVirtualItems().map(virtualRow => {
              const style = {
                position: "absolute" as CSSProperties["position"],
                top: 0,
                left: 0,
                width: "100%",
                height: virtualRow.size,
                transform: `translateY(${virtualRow.start}px)`,
              };
              const index = virtualRow.index;
              return renderNode({
                index,
                style,
                data: formattedNodes,
              });
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export const Tree = (props: Props) => {
  const {
    data,
    searchKeyword,
    getChildren,
    renderLabel,
    nodeClassName,
    treeClassName,
    emptySearchState,
    emptyState,
    controlledExpandedItems,
    onExpand,
  } = props;

  const [expandedItemsLocal, setExpandedItemsLocal] = useState<string[]>([]);
  const [focusItem, setFocusItem] = useState<string | null>(null);

  const expandedItems = controlledExpandedItems || expandedItemsLocal;

  const {
    data: treeData,
    loadingNodeValues,
    loadChildren,
  } = useGetTreeNodeChildren(data);

  const [, startTransition] = useTransition();

  const { getFlattenNodes, formatVirtualizedTreeData } = useFlattenTreeData();

  const flattenNodes = getFlattenNodes(treeData);
  const formattedNodes = formatVirtualizedTreeData(
    flattenNodes,
    treeData,
    expandedItems,
    searchKeyword
  ).filter(n => n.visible);

  const handleExpand = useCallback(
    async (node: TreeNodeType) => {
      setFocusItem(node.value);
      const nextExpandItemValues = toggleExpand<string>({
        node,
        isExpand: !node.expand,
        expandedItems,
      });
      if (
        isFunction(getChildren) &&
        !node.expand &&
        Array.isArray(node.children) &&
        (node.children.length === 0 || node.children[0]?.type === EMPTY_TYPE)
      ) {
        await loadChildren(node, getChildren);
      }
      startTransition(() => {
        controlledExpandedItems && onExpand
          ? onExpand(nextExpandItemValues)
          : setExpandedItemsLocal(nextExpandItemValues);
      });
    },
    [
      loadChildren,
      expandedItems,
      controlledExpandedItems,
      onExpand,
      getChildren,
    ]
  );

  const focusNextItem = useCallback(() => {
    const activeIndex = formattedNodes.findIndex(
      item => item.value === focusItem
    );
    if (formattedNodes.length === 0) {
      return;
    }
    const nextIndex =
      activeIndex === formattedNodes.length - 1 ? 0 : activeIndex + 1;
    const nextFocusItemValue = formattedNodes[nextIndex].value;
    setFocusItem(nextFocusItemValue);
  }, [focusItem, formattedNodes]);

  const focusPreviousItem = useCallback(() => {
    const activeIndex = formattedNodes.findIndex(
      item => item.value === focusItem
    );

    if (formattedNodes.length === 0 || activeIndex === 0) {
      return;
    }

    const prevIndex = activeIndex - 1;
    const prevFocusItemValue = formattedNodes[prevIndex].value;
    setFocusItem(prevFocusItemValue);
  }, [focusItem, formattedNodes]);

  const handleLeftArrow = useCallback(() => {
    if (!focusItem) {
      return;
    }
    const focusItemNode = getActiveItem(focusItem, flattenNodes);

    if (!focusItemNode) {
      return;
    }

    const expand = expandedItems.includes(focusItemNode.value);

    if (expand) {
      handleExpand({ ...focusItemNode, expand });
    } else if (focusItemNode?.parent) {
      setFocusItem(focusItemNode?.parent?.value);
    }
  }, [focusItem, expandedItems, handleExpand, flattenNodes]);

  const handleRightArrow = useCallback(() => {
    if (!focusItem) {
      return;
    }
    const focusItemNode = getActiveItem(focusItem, flattenNodes);

    if (!focusItemNode || !Array.isArray(focusItemNode.children)) {
      return;
    }

    const expand = expandedItems.includes(focusItemNode.value);

    if (!expand) {
      handleExpand({ ...focusItemNode, expand });
    } else {
      focusNextItem();
    }
  }, [focusItem, expandedItems, focusNextItem, flattenNodes, handleExpand]);

  const handleEnter = useCallback(() => {
    if (!focusItem) {
      return;
    }
    const focusItemNode = getActiveItem(focusItem, flattenNodes);

    if (!focusItemNode || !Array.isArray(focusItemNode.children)) {
      return;
    }

    const expand = expandedItems.includes(focusItemNode.value);

    handleExpand({ ...focusItemNode, expand });
  }, [focusItem, expandedItems, handleExpand, flattenNodes]);

  return (
    <TreeInner
      treeData={treeData}
      formattedNodes={formattedNodes}
      focusNextItem={focusNextItem}
      focusPreviousItem={focusPreviousItem}
      handleLeftArrow={handleLeftArrow}
      handleRightArrow={handleRightArrow}
      handleEnter={handleEnter}
      handleExpand={handleExpand}
      loadingNodeValues={loadingNodeValues}
      setFocusItem={setFocusItem}
      focusItem={focusItem}
      searchKeyword={searchKeyword}
      renderLabel={renderLabel}
      nodeClassName={nodeClassName}
      treeClassName={treeClassName}
      emptySearchState={emptySearchState}
      emptyState={emptyState}
      expandedItems={expandedItems}
    />
  );
};
